/**
 * Sample Contact Data for Demo/Testing
 * Provides diverse AgentCards showcasing different delegation protocols and skills
 */

import type { AgentCard } from '@/types/contact.js';
import { useContactStore } from '@/stores/contactStore.js';

export const SAMPLE_CONTACTS: AgentCard[] = [
    // Sample contacts removed - users can add their own contacts
    /* {
        // Code Assistant - A2A JSONRPC Protocol
        protocolVersion: '0.3.0',
        name: 'CodeMentor',
        description:
            'Expert code assistant specializing in code review, debugging, and refactoring. Supports multiple programming languages with deep architectural insights.',
        url: 'https://codementor.example.com/.well-known/agent-card.json',
        version: '2.1.0',
        preferredTransport: 'JSONRPC',
        defaultInputModes: ['text', 'code'],
        defaultOutputModes: ['text', 'code'],
        provider: {
            organization: 'DevTools AI',
            url: 'https://devtools-ai.example.com',
        },
        iconUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=CodeMentor&backgroundColor=4f46e5',
        documentationUrl: 'https://codementor.example.com/docs',
        capabilities: {
            streaming: true,
            pushNotifications: false,
            stateTransitionHistory: true,
        },
        skills: [
            {
                id: 'code_review',
                name: 'Code Review',
                description:
                    'Comprehensive code review focusing on best practices, performance, security, and maintainability',
                tags: ['code-quality', 'best-practices', 'security', 'performance'],
                examples: [
                    'Review this TypeScript component for potential issues',
                    'Check this API endpoint for security vulnerabilities',
                ],
                inputModes: ['code', 'text'],
                outputModes: ['text', 'code'],
            },
            {
                id: 'debug_assist',
                name: 'Debug Assistance',
                description:
                    'Help identify and fix bugs through systematic analysis of stack traces, error logs, and code behavior',
                tags: ['debugging', 'troubleshooting', 'error-analysis'],
                examples: [
                    'Why am I getting a null pointer exception here?',
                    'This async function is not resolving correctly',
                ],
                inputModes: ['code', 'text'],
                outputModes: ['text', 'code'],
            },
            {
                id: 'refactor_suggestions',
                name: 'Refactoring Suggestions',
                description:
                    'Provide actionable refactoring recommendations to improve code structure and reduce technical debt',
                tags: ['refactoring', 'architecture', 'clean-code'],
                examples: [
                    'How can I improve the structure of this module?',
                    'Suggest ways to reduce duplication in these functions',
                ],
                inputModes: ['code', 'text'],
                outputModes: ['text', 'code'],
            },
            {
                id: 'architecture_review',
                name: 'Architecture Review',
                description:
                    'Analyze system architecture and provide recommendations for scalability and maintainability',
                tags: ['architecture', 'design-patterns', 'scalability'],
                examples: [
                    'Review our microservices architecture',
                    'Is this database schema optimal for our use case?',
                ],
                inputModes: ['text', 'diagram'],
                outputModes: ['text', 'diagram'],
            },
        ],
        metadata: {
            dexto: {
                delegation: {
                    protocol: 'a2a-jsonrpc',
                    endpoint: 'https://codementor.example.com/api/v1/jsonrpc',
                    supportsSession: true,
                    supportsStreaming: true,
                },
            },
        },
    },
    {
        // Translation Agent - HTTP Simple Protocol
        protocolVersion: '0.3.0',
        name: 'LinguaLink',
        description:
            'Multi-language translation agent supporting 100+ languages. Specializes in context-aware translation with cultural nuances.',
        url: 'https://lingualink.example.com/.well-known/agent-card.json',
        version: '1.8.3',
        preferredTransport: 'HTTP+JSON',
        defaultInputModes: ['text'],
        defaultOutputModes: ['text'],
        provider: {
            organization: 'GlobalSpeak Inc',
            url: 'https://globalspeak.example.com',
        },
        iconUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=LinguaLink&backgroundColor=10b981',
        documentationUrl: 'https://lingualink.example.com/docs',
        capabilities: {
            streaming: false,
            pushNotifications: true,
            stateTransitionHistory: false,
        },
        skills: [
            {
                id: 'translate',
                name: 'Translation',
                description:
                    'Accurate translation between 100+ languages while preserving context, tone, and cultural nuances',
                tags: ['translation', 'multilingual', 'localization'],
                examples: [
                    'Translate this product description to Spanish',
                    'Convert this technical documentation to Japanese',
                ],
                inputModes: ['text'],
                outputModes: ['text'],
            },
            {
                id: 'language_detection',
                name: 'Language Detection',
                description: 'Automatically detect the language of input text with high accuracy',
                tags: ['detection', 'language-id', 'nlp'],
                examples: [
                    'What language is this text?',
                    'Detect the language and translate to English',
                ],
                inputModes: ['text'],
                outputModes: ['text'],
            },
            {
                id: 'localization',
                name: 'Localization',
                description:
                    'Adapt content for specific regions, including date formats, currencies, and cultural references',
                tags: ['i18n', 'l10n', 'cultural-adaptation'],
                examples: [
                    'Localize this UI text for Brazilian Portuguese',
                    'Adapt these marketing materials for the Asian market',
                ],
                inputModes: ['text'],
                outputModes: ['text'],
            },
        ],
        metadata: {
            dexto: {
                delegation: {
                    protocol: 'http-simple',
                    endpoint: 'https://lingualink.example.com/api/v1/translate',
                    supportsSession: false,
                    supportsStreaming: false,
                },
            },
        },
    },
    {
        // Data Analyst - A2A JSONRPC Protocol
        protocolVersion: '0.3.0',
        name: 'DataViz Pro',
        description:
            'Advanced data analysis and visualization agent. Transform raw data into actionable insights with beautiful charts and statistical analysis.',
        url: 'https://dataviz.example.com/.well-known/agent-card.json',
        version: '3.0.1',
        preferredTransport: 'JSONRPC',
        defaultInputModes: ['text', 'data'],
        defaultOutputModes: ['text', 'chart', 'data'],
        provider: {
            organization: 'Analytics AI Labs',
            url: 'https://analytics-ai.example.com',
        },
        iconUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=DataViz&backgroundColor=f59e0b',
        documentationUrl: 'https://dataviz.example.com/docs',
        capabilities: {
            streaming: true,
            pushNotifications: true,
            stateTransitionHistory: true,
        },
        skills: [
            {
                id: 'analyze_data',
                name: 'Data Analysis',
                description:
                    'Comprehensive statistical analysis including descriptive stats, correlations, and trend identification',
                tags: ['statistics', 'data-science', 'analytics', 'insights'],
                examples: [
                    'Analyze this sales data for trends',
                    'What patterns exist in this customer behavior dataset?',
                ],
                inputModes: ['data', 'text'],
                outputModes: ['text', 'data'],
            },
            {
                id: 'create_charts',
                name: 'Data Visualization',
                description:
                    'Create interactive charts and graphs including line, bar, scatter, heatmaps, and custom visualizations',
                tags: ['visualization', 'charts', 'graphs', 'dashboard'],
                examples: [
                    'Create a line chart showing revenue over time',
                    'Visualize this correlation matrix as a heatmap',
                ],
                inputModes: ['data', 'text'],
                outputModes: ['chart', 'text'],
            },
            {
                id: 'statistical_analysis',
                name: 'Statistical Analysis',
                description:
                    'Advanced statistical methods including hypothesis testing, regression analysis, and predictive modeling',
                tags: ['statistics', 'machine-learning', 'predictive-analytics'],
                examples: [
                    'Run a regression analysis on these variables',
                    'Test the hypothesis that sales increased after the campaign',
                ],
                inputModes: ['data', 'text'],
                outputModes: ['text', 'data', 'chart'],
            },
            {
                id: 'data_cleaning',
                name: 'Data Cleaning',
                description:
                    'Identify and fix data quality issues including missing values, outliers, and inconsistencies',
                tags: ['data-quality', 'preprocessing', 'etl'],
                examples: [
                    'Clean this dataset and handle missing values',
                    'Remove outliers from this time series data',
                ],
                inputModes: ['data', 'text'],
                outputModes: ['data', 'text'],
            },
        ],
        metadata: {
            dexto: {
                delegation: {
                    protocol: 'a2a-jsonrpc',
                    endpoint: 'https://dataviz.example.com/api/v1/jsonrpc',
                    supportsSession: true,
                    supportsStreaming: true,
                },
            },
        },
    },
    {
        // Research Assistant - Dexto V1 Protocol
        protocolVersion: '0.3.0',
        name: 'ResearchBot',
        description:
            'Intelligent research assistant for web search, content summarization, and fact verification. Access to real-time information and academic databases.',
        url: 'https://researchbot.example.com/.well-known/agent-card.json',
        version: '1.5.0',
        preferredTransport: 'HTTP+JSON',
        defaultInputModes: ['text'],
        defaultOutputModes: ['text', 'markdown'],
        provider: {
            organization: 'KnowledgeBase AI',
            url: 'https://knowledgebase-ai.example.com',
        },
        iconUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=ResearchBot&backgroundColor=8b5cf6',
        documentationUrl: 'https://researchbot.example.com/docs',
        capabilities: {
            streaming: true,
            pushNotifications: false,
            stateTransitionHistory: true,
        },
        skills: [
            {
                id: 'web_search',
                name: 'Web Search',
                description:
                    'Search the web for current information, news, and research papers with source citations',
                tags: ['search', 'web', 'real-time', 'sources'],
                examples: [
                    'Find the latest research on quantum computing',
                    'What are the current trends in AI development?',
                ],
                inputModes: ['text'],
                outputModes: ['text', 'markdown'],
            },
            {
                id: 'summarize',
                name: 'Content Summarization',
                description:
                    'Create concise summaries of long documents, articles, or research papers while preserving key points',
                tags: ['summarization', 'nlp', 'document-analysis'],
                examples: [
                    'Summarize this 50-page research paper',
                    'Give me the key points from these meeting notes',
                ],
                inputModes: ['text'],
                outputModes: ['text', 'markdown'],
            },
            {
                id: 'fact_check',
                name: 'Fact Verification',
                description:
                    'Verify claims and statements against reliable sources with confidence scores and citations',
                tags: ['fact-checking', 'verification', 'research', 'credibility'],
                examples: [
                    'Verify this claim about climate change',
                    'Is this statistical information accurate?',
                ],
                inputModes: ['text'],
                outputModes: ['text', 'markdown'],
            },
            {
                id: 'literature_review',
                name: 'Literature Review',
                description:
                    'Comprehensive review of academic literature on a topic with synthesis and gap analysis',
                tags: ['academic', 'research', 'literature', 'synthesis'],
                examples: [
                    'Review the literature on neural networks',
                    'What are the recent advances in renewable energy?',
                ],
                inputModes: ['text'],
                outputModes: ['text', 'markdown'],
            },
        ],
        metadata: {
            dexto: {
                delegation: {
                    protocol: 'dexto-v1',
                    endpoint: 'https://researchbot.example.com/api/v1/delegate',
                    supportsSession: true,
                    supportsStreaming: true,
                },
            },
        },
    }, */
];

/**
 * Add sample contacts to the contact store
 * Useful for first-time users or demo purposes
 *
 * @returns Array of created contact IDs
 */
export function addSampleContacts(): string[] {
    const contactStore = useContactStore.getState();
    const contactIds: string[] = [];

    // Check if sample contacts already exist
    const existingContacts = contactStore.getAllContacts();
    const existingNames = new Set(existingContacts.map((c) => c.agentCard.name));

    // Add only contacts that don't already exist
    for (const agentCard of SAMPLE_CONTACTS) {
        if (!existingNames.has(agentCard.name)) {
            const id = contactStore.addContact(agentCard, {
                tags: ['sample', 'demo'],
            });
            contactIds.push(id);
        }
    }

    return contactIds;
}

/**
 * Remove all sample contacts from the store
 */
export function removeSampleContacts(): void {
    const contactStore = useContactStore.getState();
    const allContacts = contactStore.getAllContacts();

    // Find and remove contacts that match sample contact names
    const sampleNames = new Set(SAMPLE_CONTACTS.map((c) => c.name));

    for (const contact of allContacts) {
        if (sampleNames.has(contact.agentCard.name)) {
            contactStore.removeContact(contact.id);
        }
    }
}

/**
 * Check if sample contacts are already loaded
 */
export function hasSampleContacts(): boolean {
    const contactStore = useContactStore.getState();
    const allContacts = contactStore.getAllContacts();
    const sampleNames = new Set(SAMPLE_CONTACTS.map((c) => c.name));

    return allContacts.some((c) => sampleNames.has(c.agentCard.name));
}
